import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "./axiosConfig";
import { Modal, Button, Form, Table, Tabs, Tab } from "react-bootstrap";
import "./UserPanel.css";

interface SnmpCategory {
  id: number;
  name: string;
  description: string;
}

interface SnmpCredential {
  id: number;
  user: string;
  auth_priv_level: string;
  auth_protocol: string;
  encryption_protocol: string;
  target_ip: string;
}

interface SnmpOID {
  category_id: number | null;
  metric: string;
  oid: string;
  credentials: number[];
}

type ParsedMIBItem = {
  name: string;
  oid: string;
  description?: string;
  syntax?: string;
  access?: string;
};

type SnmpTestResponse = {
  ok: boolean;
  scalar?: boolean;
  instanceOid?: string;
  value?: string | number;
  type?: string;
  rtt_ms?: number;
  error?: string;

  stage?: string;
  tried_oids?: string[];
  walk_count?: number;
  detail?: string;
  pdu_name?: string;
  pdu_type?: string;
  snmp_error?: string;
};

type TestState = SnmpTestResponse & {
  loading?: boolean;
  attempt?: number;
  status?: "idle" | "loading" | "retrying" | "ok" | "failed";
  seq?: number;
};

type IfItem = {
  ifIndex: number;
  ifName?: string;
  ifDescr?: string;
  ifAlias?: string;
  adminStatus?: number;
  operStatus?: number;
  highSpeedMbps?: number;
  speedBps?: number;
};

function normalizeAccess(v: any): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  const key = s.replace(/\s+/g, "").toLowerCase();
  const map: Record<string, string> = {
    readonly: "ReadOnly",
    "read-only": "ReadOnly",
    readwrite: "ReadWrite",
    "read-write": "ReadWrite",
    notaccessible: "NotAccessible",
    "not-accessible": "NotAccessible",
    accessiblefornotify: "AccessibleForNotify",
  };
  return map[key] || s;
}

function normalizeSyntax(raw: any): string | undefined {
  if (!raw) return undefined;
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    return (
      raw.name ||
      raw.base ||
      raw.basetype ||
      raw.tag ||
      raw.type ||
      raw.typename ||
      undefined
    );
  }
  return String(raw);
}

function normalizeParsedMIB(arr: any[]): ParsedMIBItem[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((it: any) => {
      const name =
        it.name ?? it.symbol ?? it.label ?? it.object ?? it.node ?? "";
      const oid = it.oid ?? it.OID ?? it.objectID ?? it.objectId ?? "";
      const description =
        it.description ?? it.desc ?? it.doc ?? it.comment ?? undefined;
      const syntax =
        normalizeSyntax(it.syntax) ??
        normalizeSyntax(it.type) ??
        normalizeSyntax(it.syntax?.type);
      const access =
        normalizeAccess(it.access) ??
        normalizeAccess(it.maxAccess) ??
        normalizeAccess(it.accessMode) ??
        normalizeAccess(it.statusAccess);
      return { name, oid, description, syntax, access };
    })
    .filter((x) => x.name && x.oid);
}

const SnmpOIDManager: React.FC = () => {
  const [categories, setCategories] = useState<SnmpCategory[]>([]);
  const [credentials, setCredentials] = useState<SnmpCredential[]>([]);
  const [snmpOID, setSnmpOID] = useState<SnmpOID>({
    category_id: null,
    metric: "",
    oid: "",
    credentials: [],
  });

  const [parsedMIB, setParsedMIB] = useState<ParsedMIBItem[]>([]);
  const [page, setPage] = useState(1);

  const dedupeByNameOID = (arr: ParsedMIBItem[]) => {
    const seen = new Set<string>();
    return arr.filter((x) => {
      const k = `${x.name}|${x.oid}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  const fetchAllSymbols = async () => {
    try {
      const res = await axios.get<ParsedMIBItem[]>("/mib-api/all");
      const parsed = normalizeParsedMIB(res.data || []);
      setParsedMIB(dedupeByNameOID(parsed));
      setPage(1);
    } catch (e) {
      console.error("fetchAllSymbols failed:", e);
      alert("Failed to load all MIB symbols.");
    }
  };

  useEffect(() => {
    fetchAllSymbols();
  }, []);

  const [newCategory, setNewCategory] = useState({ name: "", description: "" });
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingOIDs, setExistingOIDs] = useState<SnmpOID[]>([]);
  const [activeKey, setActiveKey] = useState<string>("add");

  const [search, setSearch] = useState("");
  const [regexMode, setRegexMode] = useState(false);
  const [showLeavesOnly, setShowLeavesOnly] = useState(true);
  const [onlyReadOnly, setOnlyReadOnly] = useState(true);
  const [syntaxFilter, setSyntaxFilter] = useState<string>("");

  const [resolveQ, setResolveQ] = useState("");

  const [testResults, setTestResults] = useState<Record<number, TestState>>({});
  const latestSeqRef = useRef<Record<number, number>>({});

  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "parsing" | "done" | "error"
  >("idle");
  const [uploadPct, setUploadPct] = useState(0);
  const isUploadingOrParsing =
    uploadStatus === "uploading" || uploadStatus === "parsing";

  const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    const fetchOIDs = async () => {
      try {
        const response = await axios.get<SnmpOID[]>("/api/snmp-oids");
        setExistingOIDs(response.data || []);
      } catch (error) {
        console.error("Error fetching existing OIDs:", error);
      }
    };
    fetchOIDs();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [categoriesRes, credentialsRes] = await Promise.all([
          axios.get<SnmpCategory[]>("/api/snmp-categories"),
          axios.get<SnmpCredential[]>("/api/snmp-credentials"),
        ]);
        setCategories(categoriesRes.data || []);
        setCredentials(credentialsRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSnmpOID((prev) => ({
      ...prev,
      [name]: name === "category_id" ? (value ? Number(value) : null) : value,
    }));
  };

  const handleCredentialChange = (id: number) => {
    setSnmpOID((prev) => ({
      ...prev,
      credentials: prev.credentials.includes(id)
        ? prev.credentials.filter((credId) => credId !== id)
        : [...prev.credentials, id],
    }));
  };

  const testCredential = async (credId: number) => {
    if (!snmpOID.oid) {
      alert("Please fill OID first.");
      return;
    }

    const nextSeq = (latestSeqRef.current[credId] || 0) + 1;
    latestSeqRef.current[credId] = nextSeq;

    setTestResults((prev) => ({
      ...prev,
      [credId]: {
        ...(prev[credId] || {}),
        seq: nextSeq,
        loading: true,
        status: "loading",
        attempt: 1,
        error: undefined,
      },
    }));

    const doOnce = async (attempt: number): Promise<boolean> => {
      try {
        const res = await axios.post<SnmpTestResponse>(
          "/api/snmp/test-oid",
          { credential_id: credId, oid: snmpOID.oid },
          { timeout: 10000 }
        );

        if (latestSeqRef.current[credId] !== nextSeq) return true;

        setTestResults((prev) => ({
          ...prev,
          [credId]: {
            ...res.data,
            loading: false,
            status: res.data.ok ? "ok" : "failed",
            attempt,
            seq: nextSeq,
          },
        }));
        return true;
      } catch (e: any) {
        if (latestSeqRef.current[credId] !== nextSeq) return true;

        const errMsg = e?.response?.data?.error || e?.message || "Test failed";

        if (attempt === 1) {
          setTestResults((prev) => ({
            ...prev,
            [credId]: {
              ...(prev[credId] || {}),
              loading: true,
              status: "retrying",
              attempt: 2,
              error: errMsg,
              seq: nextSeq,
            },
          }));
          return false;
        }

        setTestResults((prev) => ({
          ...prev,
          [credId]: {
            ok: false,
            loading: false,
            status: "failed",
            attempt,
            error: errMsg,
            seq: nextSeq,
          },
        }));
        return true;
      }
    };

    const done1 = await doOnce(1);
    if (!done1) {
      await delay(1500);
      await doOnce(2);
    }
  };

  const testSelected = async () => {
    if (snmpOID.credentials.length === 0) {
      alert("Please select at least one credential to test.");
      return;
    }
    for (const id of snmpOID.credentials) {
      await testCredential(id);
      await delay(200);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post("/api/snmp-categories", newCategory);
      alert("Category added successfully!");
      setCategories((prev) => [...prev, response.data]);
      setNewCategory({ name: "", description: "" });
      setIsCategoryModalOpen(false);
    } catch (error) {
      console.error("Error adding category:", error);
      alert("Failed to add category.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!snmpOID.category_id) {
      alert("choose category");
      return;
    }

    const selected = snmpOID.credentials;
    const failed = selected.filter(
      (id) => testResults[id] && testResults[id].ok === false
    );
    const untested = selected.filter(
      (id) => !testResults[id] || testResults[id].ok === undefined
    );
    if (failed.length > 0 || untested.length > 0) {
      const goOn = window.confirm(
        `Some selected credentials did not pass the test or were not tested.\n` +
          (failed.length ? `- Failed: ${failed.join(", ")}\n` : "") +
          (untested.length ? `- Not tested: ${untested.join(", ")}\n` : "") +
          `\nSave anyway?`
      );
      if (!goOn) return;
    }

    try {
      const response = await axios.post("/api/snmp-oid", snmpOID);
      alert("SNMP OID saved successfully!");
      console.log(response.data);
    } catch (error) {
      console.error("Error saving SNMP OID:", error);
      alert("Failed to save SNMP OID.");
    }
  };

  const resolveAndUse = async () => {
    const q = resolveQ.trim();
    if (!q) return;
    try {
      const r = await axios.get("/mib-api/resolve", { params: { q } });
      const hitArr = normalizeParsedMIB([r.data]);
      const hit = hitArr[0];
      if (hit) {
        setSnmpOID((prev) => ({ ...prev, metric: hit.name, oid: hit.oid }));
        setActiveKey("add");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        alert("Not found");
      }
    } catch (e) {
      alert("Not found");
    }
  };

  const { filtered, totalAfterFilter, syntaxOptions } = useMemo(() => {
    if (!parsedMIB || parsedMIB.length === 0) {
      return {
        filtered: [] as (ParsedMIBItem & { leaf: boolean })[],
        totalAfterFilter: 0,
        syntaxOptions: [] as string[],
      };
    }

    const oids = parsedMIB.map((x) => x.oid || "");
    const sortedOids = [...oids].sort();

    const isLeaf = (oid: string) => {
      const prefix = oid + ".";
      for (let i = 0; i < sortedOids.length; i++) {
        const cand = sortedOids[i];
        if (cand.length <= oid.length) continue;
        if (cand.startsWith(prefix)) return false;
      }
      return true;
    };

    let list = parsedMIB.map((item) => ({
      ...item,
      leaf: isLeaf(item.oid),
    }));

    const tokens = search.trim().split(/\s+/).filter(Boolean);
    if (tokens.length > 0) {
      list = list.filter((r) => {
        const target = `${r.name} ${r.oid} ${r.description || ""}`;
        if (regexMode) {
          try {
            const re = new RegExp(search, "i");
            return re.test(target);
          } catch {
            return true;
          }
        } else {
          return tokens.every((t) =>
            target.toLowerCase().includes(t.toLowerCase())
          );
        }
      });
    }

    if (showLeavesOnly) list = list.filter((r) => r.leaf);
    if (onlyReadOnly) {
      list = list.filter(
        (r) => r.access && r.access.toLowerCase().includes("readonly")
      );
    }
    if (syntaxFilter) {
      list = list.filter(
        (r) => (r.syntax || "").toLowerCase() === syntaxFilter.toLowerCase()
      );
    }

    const synSet = new Set<string>();
    parsedMIB.forEach((i) => {
      if (i.syntax) synSet.add(i.syntax);
    });
    const syntaxOptions = Array.from(synSet).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );

    list.sort((a, b) => {
      const aKey = `${a.name}`.toLowerCase();
      const bKey = `${b.name}`.toLowerCase();
      if (aKey < bKey) return -1;
      if (aKey > bKey) return 1;
      return a.oid.localeCompare(b.oid, undefined, { numeric: true });
    });

    const total = list.length;
    const start = (page - 1) * 50;
    const end = start + 50;
    list = list.slice(start, end);

    return { filtered: list, totalAfterFilter: total, syntaxOptions };
  }, [
    parsedMIB,
    search,
    regexMode,
    showLeavesOnly,
    onlyReadOnly,
    syntaxFilter,
    page,
  ]);

  const highlight = (text?: string) => {
    if (!text) return "-";
    if (!search || regexMode) return text;
    const tokens = search.trim().split(/\s+/).filter(Boolean);
    let result: (string | JSX.Element)[] = [text];
    tokens.forEach((tok, ti) => {
      const next: (string | JSX.Element)[] = [];
      result.forEach((piece, pi) => {
        if (typeof piece !== "string") {
          next.push(piece);
          return;
        }
        const parts = piece.split(new RegExp(`(${tok})`, "ig"));
        parts.forEach((p, i) => {
          if (i % 2 === 1)
            next.push(
              <mark key={`${ti}-${pi}-${i}`} className="oid-hit">
                {p}
              </mark>
            );
          else next.push(p);
        });
      });
      result = next;
    });
    return <>{result}</>;
  };

  // -----------------------------
  // Interface discovery modal
  // -----------------------------
  const IF_TEMPLATES: Array<{ label: string; metric: string; oid: string }> = [
    { label: "ifName", metric: "ifName", oid: "1.3.6.1.2.1.31.1.1.1.1" },
    { label: "ifDescr", metric: "ifDescr", oid: "1.3.6.1.2.1.2.2.1.2" },
    { label: "ifAlias", metric: "ifAlias", oid: "1.3.6.1.2.1.31.1.1.1.18" },
    { label: "ifOperStatus", metric: "ifOperStatus", oid: "1.3.6.1.2.1.2.2.1.8" },
    { label: "ifHCInOctets", metric: "ifHCInOctets", oid: "1.3.6.1.2.1.31.1.1.1.6" },
    { label: "ifHCOutOctets", metric: "ifHCOutOctets", oid: "1.3.6.1.2.1.31.1.1.1.10" },
    { label: "ifInOctets", metric: "ifInOctets", oid: "1.3.6.1.2.1.2.2.1.10" },
    { label: "ifOutOctets", metric: "ifOutOctets", oid: "1.3.6.1.2.1.2.2.1.16" },
  ];

  const [ifModalOpen, setIfModalOpen] = useState(false);

  // -----------------------------
  // Interface discovery cache (frontend only)
  // -----------------------------
  const IF_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  const ifCacheRef = useRef<Record<number, { at: number; items: IfItem[] }>>({});
  const [ifCachedAt, setIfCachedAt] = useState<number | null>(null);
  const [ifFromCache, setIfFromCache] = useState(false);

  const formatAge = (ts: number) => {
    const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    return `${hr}h ago`;
  };

  const [ifLoading, setIfLoading] = useState(false);
  const [ifError, setIfError] = useState<string>("");
  const [ifItems, setIfItems] = useState<IfItem[]>([]);
  const [ifSearch, setIfSearch] = useState("");
  const [ifOnlyUp, setIfOnlyUp] = useState(true);
  const [ifTemplateOid, setIfTemplateOid] = useState(IF_TEMPLATES[0].oid);
  const [ifTargetCredId, setIfTargetCredId] = useState<number | null>(null);

  const statusText = (v?: number) => {
    switch (v) {
      case 1:
        return "up";
      case 2:
        return "down";
      case 3:
        return "testing";
      case 4:
        return "unknown";
      case 5:
        return "dormant";
      case 6:
        return "notPresent";
      case 7:
        return "lowerLayerDown";
      default:
        return "-";
    }
  };

  // Discover Interfaces timeout
  const DISCOVER_TIMEOUT_MS = 60000;

  // Abort in-flight request
  const ifAbortRef = useRef<AbortController | null>(null);

  const getAxiosErrorMessage = (e: any) => {
    // Axios timeout
    if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
      return (
        `HTTP timeout after ${Math.round(DISCOVER_TIMEOUT_MS / 1000)}s. ` +
        `The SNMP walk may take longer (many interfaces / slow device). ` +
        `Try increasing timeout or optimize backend using SNMP bulk-walk and caching.`
      );
    }

    // Request canceled
    if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
      return "Request canceled.";
    }

    // Backend error payload
    return e?.response?.data?.error || e?.message || "discover failed";
  };

  const closeDiscoverInterfaces = () => {
    ifAbortRef.current?.abort();
    ifAbortRef.current = null;

    setIfModalOpen(false);
    setIfLoading(false);
    setIfError("");
  };

  // Unified loader name: loadDiscoverInterfaces
  const loadDiscoverInterfaces = async (force = false, resetUI = true) => {
    if (snmpOID.credentials.length === 0) {
      alert("Select at least one credential first.");
      return;
    }

    const credId = snmpOID.credentials[0];
    setIfTargetCredId(credId);

    const hit = IF_TEMPLATES.find(
      (t) => snmpOID.oid === t.oid || snmpOID.oid.startsWith(t.oid + ".")
    );
    setIfTemplateOid(hit?.oid || IF_TEMPLATES[0].oid);

    if (resetUI) {
      setIfModalOpen(true);
      setIfItems([]);
      setIfSearch("");
      setIfError("");
    } else {
      setIfError("");
    }

    // 1) Use frontend cache if valid
    if (!force) {
      const cached = ifCacheRef.current[credId];
      if (cached && Date.now() - cached.at < IF_CACHE_TTL_MS) {
        setIfItems(cached.items);
        setIfCachedAt(cached.at);
        setIfFromCache(true);
        setIfLoading(false);
        return;
      }
    }

    // 2) Cache miss or force refresh -> call backend
    ifAbortRef.current?.abort();

    const controller = new AbortController();
    ifAbortRef.current = controller;

    setIfLoading(true);
    setIfFromCache(false);

    try {
      // Use "any" config to avoid TS issues if axios types do not include "signal"
      const config: any = { timeout: DISCOVER_TIMEOUT_MS };
      config.signal = controller.signal;

      const res = await axios.post(
        "/api/snmp/discover-interfaces",
        { credential_id: credId, limit: 2000 },
        config
      );

      if (!res.data?.ok) {
        setIfError(res.data?.error || "discover failed");
        return;
      }

      const items: IfItem[] = res.data?.items || [];
      setIfItems(items);

      const now = Date.now();
      ifCacheRef.current[credId] = { at: now, items };
      setIfCachedAt(now);
      setIfFromCache(false);
    } catch (e: any) {
      setIfError(getAxiosErrorMessage(e));
    } finally {
      setIfLoading(false);

      if (ifAbortRef.current === controller) {
        ifAbortRef.current = null;
      }
    }
  };

  // Dedicated onClick handler to avoid TS2322
  const handleDiscoverInterfacesClick = () => {
    void loadDiscoverInterfaces(false, true);
  };

  const filteredIfItems = useMemo(() => {
    const q = ifSearch.trim().toLowerCase();
    let list = [...ifItems];
    if (ifOnlyUp) {
      list = list.filter((x) => x.operStatus === 1);
    }
    if (q) {
      list = list.filter((x) => {
        const s =
          `${x.ifIndex} ${x.ifName || ""} ${x.ifDescr || ""} ${x.ifAlias || ""}`.toLowerCase();
        return s.includes(q);
      });
    }
    return list;
  }, [ifItems, ifSearch, ifOnlyUp]);

  const replaceIndexInMetric = (metric: string, idx: number) => {
    const m = metric.trim();
    if (!m) return String(idx);
    const parts = m.split(".");
    const last = parts[parts.length - 1];
    if (/^\d+$/.test(last)) {
      parts[parts.length - 1] = String(idx);
      return parts.join(".");
    }
    return `${m}.${idx}`;
  };

  const replaceIndexInOID = (oid: string, idx: number) => {
    const o = oid.trim();
    if (!o) return String(idx);
    const tpl = IF_TEMPLATES.find((t) => o === t.oid || o.startsWith(t.oid + "."));
    const base = tpl ? tpl.oid : o;
    if (base.endsWith("." + String(idx))) return base;
    if (tpl && o.startsWith(tpl.oid + ".")) {
      return `${tpl.oid}.${idx}`;
    }
    return `${base}.${idx}`;
  };

  // IMPORTANT: do NOT name this "useXxx" to avoid ESLint hook detection
  const applyInterface = (it: IfItem) => {
    const tpl =
      IF_TEMPLATES.find((t) => t.oid === ifTemplateOid) || IF_TEMPLATES[0];
    setSnmpOID((prev) => ({
      ...prev,
      metric: replaceIndexInMetric(tpl.metric, it.ifIndex),
      oid: replaceIndexInOID(tpl.oid, it.ifIndex),
    }));
    setIfModalOpen(false);
    setActiveKey("add");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentIfTarget = useMemo(() => {
    if (!ifTargetCredId) return null;
    return credentials.find((x) => x.id === ifTargetCredId) || null;
  }, [ifTargetCredId, credentials]);

  return (
    <div className="container">
      <h1>SNMP OID Manager</h1>

      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <Tabs
          activeKey={activeKey}
          onSelect={(k) => k && setActiveKey(k)}
          className="mb-3 my-dark-tabs"
        >
          {/* ---------------- Add ---------------- */}
          <Tab eventKey="add" title="Add SNMP OID">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="category_id">Category</label>
                <select
                  id="category_id"
                  name="category_id"
                  value={snmpOID.category_id || ""}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="" disabled>
                    choose category
                  </option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="link"
                  onClick={() => setIsCategoryModalOpen(true)}
                >
                  Add New Category
                </Button>
              </div>

              <div className="form-group">
                <label htmlFor="metric">Metric</label>
                <input
                  type="text"
                  id="metric"
                  name="metric"
                  value={snmpOID.metric}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="oid">OID</label>
                <input
                  type="text"
                  id="oid"
                  name="oid"
                  value={snmpOID.oid}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="d-flex gap-2 align-items-center mb-2">
                <span className="text-light">Credentials</span>
                <Button variant="outline-info" size="sm" onClick={testSelected}>
                  Test selected
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleDiscoverInterfacesClick}
                  title="Discover interface list (ifIndex ↔ ifName) from the first selected credential"
                >
                  Discover Interfaces
                </Button>
                {snmpOID.credentials.length > 1 && (
                  <span className="text-warning small">
                    Uses the first selected target for discovery.
                  </span>
                )}
              </div>

              <Table striped bordered hover size="sm" responsive variant="dark">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>User</th>
                    <th>AuthPriv Level</th>
                    <th>Auth Protocol</th>
                    <th>Encryption Protocol</th>
                    <th>Target IP</th>
                    <th style={{ width: 90 }}>Test</th>
                    <th>Result</th>
                    <th style={{ width: 70 }}>Pass</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((credential) => {
                    const tr = testResults[credential.id];
                    return (
                      <tr key={credential.id}>
                        <td>
                          <input
                            type="checkbox"
                            id={`credential_${credential.id}`}
                            checked={snmpOID.credentials.includes(credential.id)}
                            onChange={() => handleCredentialChange(credential.id)}
                          />
                        </td>
                        <td>{credential.user}</td>
                        <td>{credential.auth_priv_level}</td>
                        <td>{credential.auth_protocol}</td>
                        <td>{credential.encryption_protocol}</td>
                        <td>{credential.target_ip}</td>

                        <td>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={tr?.loading || tr?.status === "retrying"}
                            onClick={() => testCredential(credential.id)}
                          >
                            {tr?.status === "retrying"
                              ? "Retrying…"
                              : tr?.loading
                              ? "Testing…"
                              : "Test"}
                          </Button>
                        </td>

                        <td>
                          {tr ? (
                            tr.status === "loading" ? (
                              <span>Testing…</span>
                            ) : tr.status === "retrying" ? (
                              <span className="text-warning">Retrying… (1/2)</span>
                            ) : tr.ok ? (
                              <div>
                                <div>OID: {tr.instanceOid || snmpOID.oid}</div>
                                <div>
                                  Value: <strong>{String(tr.value)}</strong>{" "}
                                  ({tr.type || "-"})
                                </div>
                                <div className="text-muted small">
                                  RTT: {tr.rtt_ms ?? "-"} ms
                                </div>
                                {tr.stage && (
                                  <div className="text-muted small">
                                    Stage: {tr.stage}
                                    {typeof tr.walk_count === "number"
                                      ? `, WalkCount: ${tr.walk_count}`
                                      : ""}
                                  </div>
                                )}
                                {tr.tried_oids && tr.tried_oids.length > 0 && (
                                  <div className="text-muted small">
                                    Tried: {tr.tried_oids.join(", ")}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-danger">
                                <div>{tr.error || "Failed"}</div>
                                {tr.detail && (
                                  <div
                                    className="text-muted small"
                                    style={{ whiteSpace: "pre-wrap" }}
                                  >
                                    {tr.detail}
                                  </div>
                                )}
                              </div>
                            )
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>

                        <td style={{ textAlign: "center" }}>
                          {tr?.ok === true ? "✅" : tr?.ok === false ? "❌" : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <Button type="submit" variant="primary">
                Save SNMP OID
              </Button>
            </form>
          </Tab>

          {/* ---------------- Import ---------------- */}
          <Tab eventKey="import" title="Import MIB">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData();
                const fileInput = document.getElementById(
                  "mibFile"
                ) as HTMLInputElement;

                if (!fileInput.files || fileInput.files.length === 0) {
                  alert("Please select a .mib file");
                  return;
                }

                const file = fileInput.files[0];
                formData.append("file", file);

                try {
                  setUploadStatus("uploading");
                  setUploadPct(0);

                  const uploadRes = await axios.post(
                    "/mib-api/upload-mib",
                    formData,
                    {
                      headers: { "Content-Type": "multipart/form-data" },
                      onUploadProgress: (evt) => {
                        if (!evt.total) return;
                        const pct = Math.round((evt.loaded * 100) / evt.total);
                        setUploadPct(pct);
                      },
                    }
                  );

                  const filename = uploadRes.data?.filename;
                  if (!filename) {
                    setUploadStatus("error");
                    alert("Upload failed.");
                    return;
                  }

                  setUploadStatus("parsing");
                  await axios.get(
                    `/mib-api/parse-mib?file=${encodeURIComponent(filename)}`
                  );

                  await fetchAllSymbols();
                  setUploadStatus("done");
                  alert("MIB parsed and merged into the global set.");
                } catch (err) {
                  console.error("MIB upload/parse failed:", err);
                  setUploadStatus("error");
                  alert("Upload or parsing failed.");
                } finally {
                  setTimeout(() => setUploadStatus("idle"), 2000);
                }
              }}
            >
              <Form.Group controlId="formMIBFile">
                <Form.Label className="text-light">Upload MIB File</Form.Label>
                <Form.Control
                  type="file"
                  id="mibFile"
                  accept=".mib,.my"
                  className="custom-dark-file"
                  disabled={isUploadingOrParsing}
                />
              </Form.Group>

              <Button
                type="submit"
                className="mt-2"
                variant="outline-primary"
                disabled={isUploadingOrParsing}
              >
                {uploadStatus === "uploading"
                  ? `Uploading ${uploadPct}%`
                  : uploadStatus === "parsing"
                  ? "Parsing…"
                  : "Upload & Parse"}
              </Button>

              {uploadStatus !== "idle" && (
                <div className="mt-2" style={{ maxWidth: 420 }}>
                  {uploadStatus === "uploading" && (
                    <>
                      <div className="text-light small mb-1">
                        Uploading… {uploadPct}%
                      </div>
                      <div className="progress">
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${uploadPct}%` }}
                          aria-valuenow={uploadPct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {uploadPct}%
                        </div>
                      </div>
                    </>
                  )}
                  {uploadStatus === "parsing" && (
                    <div className="text-light small">Parsing MIB…</div>
                  )}
                  {uploadStatus === "done" && (
                    <div className="text-success small">Done.</div>
                  )}
                  {uploadStatus === "error" && (
                    <div className="text-danger small">Failed.</div>
                  )}
                </div>
              )}
            </form>

            <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
              <Form.Control
                placeholder="Search name/oid/description…  (space = AND)   Enable Regex to use regex"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ minWidth: 280 }}
              />
              <Form.Check
                type="switch"
                id="regexMode"
                label="Regex"
                checked={regexMode}
                onChange={(e) => {
                  setRegexMode(e.target.checked);
                  setPage(1);
                }}
                className="text-light"
              />
              <Form.Check
                type="switch"
                id="leavesOnly"
                label="Leaves only"
                checked={showLeavesOnly}
                onChange={(e) => {
                  setShowLeavesOnly(e.target.checked);
                  setPage(1);
                }}
                className="text-light"
              />
              <Form.Check
                type="switch"
                id="onlyReadOnly"
                label="Read-only"
                checked={onlyReadOnly}
                onChange={(e) => {
                  setOnlyReadOnly(e.target.checked);
                  setPage(1);
                }}
                className="text-light"
              />
              <Form.Select
                value={syntaxFilter}
                onChange={(e) => {
                  setSyntaxFilter(e.target.value);
                  setPage(1);
                }}
                style={{ maxWidth: 180 }}
              >
                <option value="">All Syntax</option>
                {syntaxOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>

              <div className="d-flex align-items-center gap-2 ms-auto">
                <Form.Control
                  placeholder="Resolve name or numeric OID (e.g. panSysModel)"
                  value={resolveQ}
                  onChange={(e) => setResolveQ(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={resolveAndUse}
                >
                  Resolve → Use
                </Button>
              </div>

              <div className="text-light small ms-3">
                Showing {filtered.length ? (page - 1) * 50 + 1 : 0}–
                {(page - 1) * 50 + filtered.length} / {totalAfterFilter}
              </div>
            </div>

            <div className="mt-3">
              <Table striped bordered hover size="sm" responsive variant="dark">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Metric</th>
                    <th style={{ minWidth: 200 }}>OID</th>
                    <th style={{ minWidth: 300 }}>Description</th>
                    <th style={{ width: 120 }}>Syntax</th>
                    <th style={{ width: 120 }}>Access</th>
                    <th style={{ width: 70 }}>Leaf</th>
                    <th style={{ width: 140 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((item, idx) => (
                      <tr key={`${item.oid}-${idx}`}>
                        <td>{highlight(item.name)}</td>
                        <td>{highlight(item.oid)}</td>
                        <td title={item.description || ""}>
                          {highlight(item.description)}
                        </td>
                        <td>{item.syntax || "-"}</td>
                        <td>{item.access || "-"}</td>
                        <td>{(item as any).leaf ? "✓" : ""}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => {
                              setSnmpOID((prev) => ({
                                ...prev,
                                metric: item.name,
                                oid: item.oid,
                              }));
                              setActiveKey("add");
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            Use this OID
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center" }}>
                        No MIB parsed yet or no match.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {totalAfterFilter > 50 && (
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    ← Prev
                  </Button>
                  <div className="text-light small">
                    Page {page} / {Math.ceil(totalAfterFilter / 50)}
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page * 50 >= totalAfterFilter}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next →
                  </Button>
                </div>
              )}
            </div>
          </Tab>

          {/* ---------------- View ---------------- */}
          <Tab eventKey="view" title="View Existing OIDs">
            <Table striped bordered hover size="sm" responsive variant="dark">
              <thead>
                <tr>
                  <th>OID</th>
                  <th>Metric</th>
                  <th>Credentials</th>
                </tr>
              </thead>
              <tbody>
                {existingOIDs.length > 0 ? (
                  existingOIDs.map((oid, index) => (
                    <tr key={`${oid.oid}-${index}`}>
                      <td>{oid.oid}</td>
                      <td>{oid.metric}</td>
                      <td>
                        {oid.credentials.map((credId) => (
                          <div key={credId}>
                            {credentials.find((c) => c.id === credId)?.target_ip ||
                              "Unknown"}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center" }}>
                      not found OID
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Tab>
        </Tabs>
      )}

      {/* Category modal */}
      <Modal show={isCategoryModalOpen} onHide={() => setIsCategoryModalOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCategorySubmit}>
            <Form.Group controlId="formCategoryName">
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newCategory.name}
                onChange={(e) =>
                  setNewCategory((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group controlId="formCategoryDescription" className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={newCategory.description}
                onChange={(e) =>
                  setNewCategory((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Form.Group>
            <Button variant="primary" type="submit" className="mt-3">
              Add Category
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsCategoryModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Interface discovery modal */}
      <Modal show={ifModalOpen} onHide={closeDiscoverInterfaces} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Discover Interfaces</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
            <div className="text-muted small">
              Target:{" "}
              <strong>
                {currentIfTarget?.target_ip || "-"} ({currentIfTarget?.user || "-"})
              </strong>
            </div>

            <Form.Select
              value={ifTemplateOid}
              onChange={(e) => setIfTemplateOid(e.target.value)}
              style={{ maxWidth: 260 }}
            >
              {IF_TEMPLATES.map((t) => (
                <option key={t.oid} value={t.oid}>
                  Template: {t.label}
                </option>
              ))}
            </Form.Select>

            <Form.Control
              placeholder="Search ifName/ifDescr/ifAlias…"
              value={ifSearch}
              onChange={(e) => setIfSearch(e.target.value)}
              style={{ minWidth: 260 }}
            />

            {/* push right side controls */}
            <div className="ms-auto d-flex align-items-center gap-2">
              {ifCachedAt && (
                <div className={`small ${ifFromCache ? "text-info" : "text-muted"}`}>
                  {ifFromCache ? "Cached" : "Fetched"}: {formatAge(ifCachedAt)}
                </div>
              )}

              <Button
                size="sm"
                variant="outline-info"
                disabled={ifLoading}
                onClick={() => loadDiscoverInterfaces(true, false)}
                title="Force re-walk interfaces (ignore cache)"
              >
                Refresh
              </Button>

              <Form.Check
                type="switch"
                id="ifOnlyUp"
                label="Only up"
                checked={ifOnlyUp}
                onChange={(e) => setIfOnlyUp(e.target.checked)}
              />
            </div>
          </div>

          {ifLoading ? (
            <div>Loading interfaces…</div>
          ) : ifError ? (
            <div className="text-danger">{ifError}</div>
          ) : (
            <Table striped bordered hover size="sm" responsive variant="dark">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ifIndex</th>
                  <th style={{ minWidth: 120 }}>ifName</th>
                  <th style={{ minWidth: 180 }}>ifDescr</th>
                  <th style={{ minWidth: 180 }}>ifAlias</th>
                  <th style={{ width: 90 }}>Oper</th>
                  <th style={{ width: 90 }}>Admin</th>
                  <th style={{ width: 120 }}>HighSpeed</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredIfItems.length > 0 ? (
                  filteredIfItems.map((it) => (
                    <tr key={it.ifIndex}>
                      <td>{it.ifIndex}</td>
                      <td>{it.ifName || "-"}</td>
                      <td>{it.ifDescr || "-"}</td>
                      <td>{it.ifAlias || "-"}</td>
                      <td>{statusText(it.operStatus)}</td>
                      <td>{statusText(it.adminStatus)}</td>
                      <td>
                        {typeof it.highSpeedMbps === "number"
                          ? `${it.highSpeedMbps} Mbps`
                          : typeof it.speedBps === "number"
                          ? `${Math.round(it.speedBps / 1e6)} Mbps`
                          : "-"}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => applyInterface(it)}
                        >
                          Use
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center" }}>
                      No interfaces.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDiscoverInterfaces}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SnmpOIDManager;
